/**
 * Runtime configuration that is safe to ship to the browser.
 *
 * Values are read from process.env on the server at request time (one image serves every environment,
 * with the real values mounted at /config/.env), and handed to the browser through ConfigProvider.
 * Pages router: _app.getInitialProps + ConfigProvider in _app. App router: the root layout.
 *
 * Only public values may be read in this module. Anything server-only lives in ./server, which must never
 * be imported from code that ends up in a client bundle (enforced by eslint's no-restricted-imports).
 */
export interface ClientConfig {
  adyenEnv: string
  /** Search-only key, public by design. */
  algoliaAPIKey: string
  algoliaAppID: string
  algoliaIndexSuffix: string
  googleTagManagerKey: string
  /** The public GraphQL endpoint browsers talk to. The internal one is server-only, see ./server. */
  graphqlEndpoint: string
  graphqlEndpointDomainProxyExtra: string
  recaptchaSiteKey: string
  redirectEndpoint: string
  sentryDSN: string
  sentryEnvironment: string
  sentryRelease: string
}

// A globalThis slot rather than a module variable: the custom server (dist/), the Next server bundle and
// later instrumentation.ts are separate module graphs inside one process and must see the same value.
const slot = globalThis as typeof globalThis & { thClientConfig?: ClientConfig }

/**
 * Builds the browser-safe config from environment variables. Every key is listed explicitly on purpose:
 * never spread process.env into this object.
 */
/** process.env without Next's required NODE_ENV, so tests can pass plain objects. */
export type Env = Record<string, string | undefined>

export const buildClientConfig = (env: Env = process.env): ClientConfig => ({
  adyenEnv: env.ADYEN_ENV || '',
  algoliaAPIKey: env.ALGOLIA_API_KEY || '',
  algoliaAppID: env.ALGOLIA_APP_ID || '',
  algoliaIndexSuffix: env.ALGOLIA_INDEX_SUFFIX ? `_${env.ALGOLIA_INDEX_SUFFIX}` : '',
  googleTagManagerKey: env.GOOGLE_TAG_MANAGER_KEY || '',
  graphqlEndpoint: env.GRAPHQL_ENDPOINT_CLIENT || env.GRAPHQL_ENDPOINT || '',
  graphqlEndpointDomainProxyExtra: env.GRAPHQL_ENDPOINT_DOMAIN_PROXY_EXTRA || '',
  recaptchaSiteKey: env.RECAPTCHA_SITE_KEY || '',
  redirectEndpoint: env.REDIRECT_ENDPOINT || '',
  sentryDSN: env.SENTRY_DSN || '',
  sentryEnvironment: env.SENTRY_ENVIRONMENT || '',
  sentryRelease: env.SENTRY_RELEASE || '',
})

export const setClientConfig = (config: ClientConfig): void => {
  slot.thClientConfig = config
}

/** Test helper: forget the cached config so the next getClientConfig() rebuilds it. */
export const resetClientConfig = (): void => {
  delete slot.thClientConfig
}

/**
 * Non-React access (Apollo, Algolia, Sentry, _document). On the server the config is built from the
 * environment on first use; in the browser it is only available once ConfigProvider has been rendered.
 */
export const getClientConfig = (): ClientConfig => {
  if (slot.thClientConfig) {
    return slot.thClientConfig
  }

  if (typeof window === 'undefined') {
    const built = buildClientConfig()
    slot.thClientConfig = built
    return built
  }

  throw new Error('[config] client config is not available until ConfigProvider has rendered')
}
